import { z } from 'zod';
import type { RequestMetadataRow } from '../electron/search-db';
import type { ProxyHistoryRow } from '@terra/shared-dto-chat-types';
import {
  HttpHeadersRecordSchema,
  parseStoredJsonField,
} from './history-metadata-schemas';

const ProxyHistoryTimingSchema = z.object({
  total: z.number().optional(),
  duration: z.number().optional(),
});

const MAX_BODY_LENGTH = 10_000;

export function truncateProxyHistoryBody({
  body,
}: {
  body: string | undefined;
}): string | undefined {
  if (!body) {
    return undefined;
  }

  if (body.length <= MAX_BODY_LENGTH) {
    return body;
  }

  return `${body.slice(0, MAX_BODY_LENGTH)}…`;
}

function parseDurationMs({ timing }: { timing: string | null }): number | null {
  if (!timing) {
    return null;
  }

  try {
    const parsed = ProxyHistoryTimingSchema.safeParse(JSON.parse(timing));
    if (!parsed.success) {
      return null;
    }

    if (typeof parsed.data.total === 'number') {
      return parsed.data.total;
    }
    if (typeof parsed.data.duration === 'number') {
      return parsed.data.duration;
    }
  } catch {
    return null;
  }

  return null;
}

function parseStoredHeaders({
  headersJson,
}: {
  headersJson: string | null;
}): Record<string, string> | undefined {
  const parsed = parseStoredJsonField(HttpHeadersRecordSchema, headersJson);
  if (!parsed) {
    return undefined;
  }

  const headers = Object.fromEntries(
    Object.entries(parsed).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(', ') : value,
    ]),
  );

  return Object.keys(headers).length > 0 ? headers : undefined;
}

export function mapRequestMetadataRowToProxyHistoryRow({
  row,
  requestBody,
  responseBody,
  includeHeaders = false,
}: {
  row: RequestMetadataRow;
  requestBody?: string;
  responseBody?: string;
  includeHeaders?: boolean;
}): ProxyHistoryRow {
  return {
    id: row.id,
    method: row.method,
    url: row.url,
    statusCode: row.status_code ?? null,
    statusMessage: row.status_message ?? null,
    timestamp: row.timestamp,
    durationMs: parseDurationMs({ timing: row.timing }),
    requestBodyPreview: row.body_preview ?? null,
    responseBodyPreview: row.response_body_preview ?? null,
    source: row.source ?? null,
    wasIntercepted: row.was_intercepted === 1,
    wasProxyModified: row.was_proxy_modified === 1,
    ...(includeHeaders
      ? {
          requestHeaders: parseStoredHeaders({
            headersJson: row.request_headers,
          }),
          responseHeaders: parseStoredHeaders({
            headersJson: row.response_headers,
          }),
        }
      : {}),
    requestBody: truncateProxyHistoryBody({ body: requestBody }),
    responseBody: truncateProxyHistoryBody({ body: responseBody }),
  };
}
